// web/src/components/FamilyShareCard.tsx
// One-shot dashboard discovery card for the family-share feature
// (Horizon 1 / H1.3).
//
// Renders only for Stats/Reports owners who haven't dismissed it. The
// feature ships in #19 but discovery is buried under Settings → Family
// members; this card surfaces it on the dashboard with a single CTA.
//
// Persistence: client PATCHes /api/v1/parent/me with `{ familyShareSeen:
// true }`. Server stamps `familyShareSeenAt` so the card never reappears
// for that parent — including on other devices.
//
// Surfacing rules:
//   - parent.plan IN (stats, reports)
//   - parent.household?.role !== 'co_parent'  (co-parents can't invite)
//   - parent.familyShareSeenAt is undefined
//   - (caller already filters; this component just renders)

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParentApi } from '../api/parent';
import type { Parent } from '../types';

interface Props {
  parent: Parent;
  onDismissed: () => void;
}

/**
 * True iff this parent should see the family-share discovery card right
 * now. Centralised so callers only have to render `<FamilyShareCard />`
 * inside `{shouldShowFamilyShareCard(parent) && (...)}`.
 */
export function shouldShowFamilyShareCard(parent: Parent | null | undefined): boolean {
  if (!parent) return false;
  if (parent.plan !== 'stats' && parent.plan !== 'reports') return false;
  if (parent.household?.role === 'co_parent') return false;
  if (parent.familyShareSeenAt) return false;
  return true;
}

export function FamilyShareCard({ parent: _parent, onDismissed }: Props) {
  // parent currently informs only the shouldShow gate (called outside) — we
  // keep the prop on the public API so the caller's shape is obvious, but
  // the component itself doesn't need to read it. Underscore-prefix to
  // satisfy the unused-var rule without dropping the prop.
  void _parent;
  const parentApi = useParentApi();
  const [dismissing, setDismissing] = useState(false);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await parentApi.updateMe({ familyShareSeen: true });
    } catch {
      // Don't block UX on a failed PATCH — the card hides locally either
      // way. Worst case the card reappears on next dashboard load; the
      // user can tap × again. Owner can always invite from Settings.
    }
    onDismissed();
  };

  return (
    <div
      style={{
        marginTop: 'var(--space-base)',
        padding: 'var(--space-lg)',
        borderRadius: 16,
        background:
          'linear-gradient(135deg, var(--green) 0%, color-mix(in srgb, var(--green) 75%, var(--gold) 25%) 100%)',
        color: 'var(--ink-on-green)',
        display: 'flex',
        gap: 'var(--space-base)',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        disabled={dismissing}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 28,
          height: 28,
          border: 'none',
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.18)',
          color: 'var(--ink-on-green)',
          cursor: 'pointer',
          opacity: dismissing ? 0.5 : 1,
        }}
      >
        ✕
      </button>

      <div style={{ flex: '1 1 280px', minWidth: 0, paddingRight: 36 }}>
        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--micro)',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--gold)',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Included on your plan
        </div>
        <h3
          style={{
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            textTransform: 'uppercase',
            letterSpacing: '-0.01em',
            fontSize: 'clamp(1.3rem, 3.4vw, 1.8rem)',
            lineHeight: 1.05,
            margin: '0 0 8px',
            // Global h1-h3 rule paints headings --green; on this green
            // card that made the headline invisible. Force the on-green ink.
            color: 'var(--ink-on-green)',
          }}
        >
          Add a co-parent for free
        </h3>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, opacity: 0.92 }}>
          Your partner can record matches, view stats, and never miss a moment.
          One free seat per household.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
        <Link
          to="/settings#family-members"
          onClick={() => {
            // Dismiss when they engage — they've discovered the feature.
            // Fire-and-forget; navigation is the user feedback.
            void parentApi.updateMe({ familyShareSeen: true }).catch(() => undefined);
            onDismissed();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 18px',
            borderRadius: 999,
            background: 'var(--cream)',
            color: 'var(--green)',
            fontFamily: 'var(--font-body)',
            fontStyle: 'italic',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          + Invite
        </Link>
      </div>
    </div>
  );
}
